import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useSelector } from 'react-redux';
import { API_BASE_URL } from '../../utils/apiConfig';
import { getBillingStudents, markBilled } from '../../utils/billingApi';
import { showSuccess, showError } from '../../components/CustomAlert';
import { getSecureFileUrlWithToken } from '../../utils/secureFileAccess';
import { FaSearch, FaTimes, FaPlus, FaCalendarAlt, FaFileAlt } from 'react-icons/fa';
import { format, addDays } from 'date-fns';
import Modal from 'react-modal';

const Container = styled.div`
  padding: 20px;
  background: #f8f9fa;
  min-height: 100vh;
  width: 100%;
  max-width: none;
  box-sizing: border-box;
`;

const Title = styled.h1`
  font-size: 24px;
  font-weight: 600;
  color: #333;
  margin-bottom: 30px;
`;

const FilterContainer = styled.div`
  background: white;
  padding: 25px;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  margin-bottom: 30px;
`;

const FilterRow = styled.div`
  display: flex;
  gap: 20px;
  align-items: end;
  flex-wrap: wrap;
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
    gap: 15px;
  }
`;

const FilterGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 200px;
`;

const Label = styled.label`
  font-weight: 500;
  color: #333;
  font-size: 14px;
`;

const Select = styled.select`
  padding: 10px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
  background: white;
  color: #333;
  &:focus {
    outline: none;
    border-color: #007bff;
    box-shadow: 0 0 0 2px rgba(0,123,255,0.25);
  }
`;

const SearchButton = styled.button`
  border: none;
  padding: 12px 20px;
  border-radius: 8px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 600;
  background: #007bff;
  color: white;
  &:hover {
    background: #0056b3;
  }
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const SearchInputContainer = styled.div`
  position: relative;
  width: 100%;
  margin-top: 20px;
`;

const SearchInput = styled.input`
  height: 48px;
  padding: 12px 45px;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  font-size: 1rem;
  width: 100%;
  background: white;
  &:focus {
    outline: none;
    border-color: #007bff;
    box-shadow: 0 0 0 2px rgba(0,123,255,0.25);
  }
`;

const SearchIcon = styled.div`
  position: absolute;
  left: 15px;
  top: 50%;
  transform: translateY(-50%);
  color: #adb5bd;
  pointer-events: none;
`;

const ClearSearchIcon = styled.div`
  position: absolute;
  right: 15px;
  top: 50%;
  transform: translateY(-50%);
  color: #adb5bd;
  cursor: pointer;
  &:hover { color: #6c757d; }
`;

const ContentContainer = styled.div`
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  min-height: 400px;
  width: 100%;
  max-width: none;
  overflow-x: auto;
  box-sizing: border-box;
`;

const TableWrapper = styled.div`
  width: 100%;
  overflow-x: auto;
`;

const Table = styled.table`
  width: 100%;
  min-width: 1200px;
  border-collapse: collapse;
  table-layout: auto;
`;

const Th = styled.th`
  padding: 12px;
  text-align: left;
  background: #f8f9fa;
  border-bottom: 2px solid #dee2e6;
  font-weight: 600;
  color: #333;
`;

const Td = styled.td`
  padding: 12px;
  border-bottom: 1px solid #dee2e6;
  color: #333;
`;

const TdCourse = styled(Td)`
  width: 20%;
  max-width: 20%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const AttendanceVoucherCell = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
`;

const ActionButton = styled.button`
  border: none;
  padding: 8px 14px;
  border-radius: 4px;
  font-size: 13px;
  cursor: pointer;
  margin-right: 8px;
  margin-bottom: 4px;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  &:hover {
    opacity: 0.9;
  }
`;

const MarkBilledButton = styled(ActionButton)`
  background: #28a745;
  color: white;
  &:hover { background: #218838; }
`;

const BillingCell = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 6px;
`;

const InvoiceLink = styled.button`
  background: none;
  border: none;
  padding: 0;
  font-size: 13px;
  color: #007bff;
  cursor: pointer;
  text-decoration: underline;
  text-align: left;
  &:hover {
    color: #0056b3;
  }
`;

const AttendanceLinkButton = styled(ActionButton)`
  background: #007bff;
  color: white;
  &:hover { background: #0056b3; }
`;

const VoucherButton = styled(ActionButton)`
  background: #6f42c1;
  color: white;
  &:hover { background: #5a32a3; }
`;

const LoadingSpinner = styled.div`
  text-align: center;
  padding: 40px;
  color: #6c757d;
`;

const NoRecordsMessage = styled.div`
  color: #6c757d;
  font-size: 16px;
  padding: 40px;
  text-align: center;
`;

const PaginationContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px;
  border-top: 1px solid #e9ecef;
`;

const PaginationInfo = styled.div`
  color: #6c757d;
  font-size: 0.9rem;
`;

const PaginationButtons = styled.div`
  display: flex;
  gap: 10px;
`;

const PageButton = styled.button`
  padding: 8px 16px;
  border: 1px solid #dee2e6;
  background: white;
  color: #495057;
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.9rem;
  &:hover:not(:disabled) {
    background: #f8f9fa;
    border-color: #007bff;
    color: #007bff;
  }
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

// Modal styles
const ModalStyles = {
  overlay: { background: 'rgba(0,0,0,0.5)' },
  content: {
    top: '50%',
    left: '50%',
    right: 'auto',
    bottom: 'auto',
    transform: 'translate(-50%, -50%)',
    maxWidth: '480px',
    width: '90%',
    borderRadius: '8px',
    padding: '24px'
  }
};

const ModalTitle = styled.h2`
  margin: 0 0 20px 0;
  font-size: 1.25rem;
  color: #333;
`;

const FormGroup = styled.div`
  margin-bottom: 16px;
`;

const FormLabel = styled.label`
  display: block;
  font-weight: 500;
  margin-bottom: 6px;
  font-size: 14px;
  color: #333;
`;

const FileInput = styled.input`
  display: block;
  width: 100%;
  padding: 8px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
`;

const ModalActions = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  margin-top: 24px;
`;

const CancelButton = styled.button`
  padding: 10px 20px;
  border: 1px solid #dee2e6;
  background: white;
  color: #495057;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
  &:hover { background: #f8f9fa; }
`;

const SubmitButton = styled.button`
  padding: 10px 20px;
  border: none;
  background: #007bff;
  color: white;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
  &:hover { background: #0056b3; }
  &:disabled { opacity: 0.6; cursor: not-allowed; }
`;

const Billing = () => {
  const [filters, setFilters] = useState({ course: '', session: '' });
  const [searchTerm, setSearchTerm] = useState('');
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, totalPages: 0 });
  const [selectedSession, setSelectedSession] = useState(null);
  const [trainingPeriodByStudent, setTrainingPeriodByStudent] = useState({});
  const [modalOpen, setModalOpen] = useState(false);
  const [modalStudent, setModalStudent] = useState(null);
  const [invoiceFile, setInvoiceFile] = useState(null);
  const now = new Date();
  const [billingPeriodMonth, setBillingPeriodMonth] = useState(String(now.getMonth() + 1));
  const [billingPeriodYear, setBillingPeriodYear] = useState(String(now.getFullYear()));
  const [submitting, setSubmitting] = useState(false);

  const { courses: masterCourses, sessions: masterSessions } = useSelector(state => state.masterData);
  const token = useSelector(state => state.auth?.token) || localStorage.getItem('token');

  const filteredSessions = filters.course
    ? masterSessions.filter(s => s.course_id == filters.course || s.course_id === parseInt(filters.course, 10))
    : [];

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
    if (field === 'course') {
      setFilters(prev => ({ ...prev, session: '' }));
      setSelectedSession(null);
    }
    if (field === 'session') {
      const session = masterSessions.find(s => s.id.toString() === value);
      setSelectedSession(session || null);
    }
  };

  const fetchBillingStudents = async (page = 1) => {
    setLoading(true);
    try {
      const params = { page, limit: 20 };
      if (filters.session) params.sessionId = filters.session;
      if (filters.course) params.course = filters.course;
      if (searchTerm.trim()) params.search = searchTerm.trim();
      const data = await getBillingStudents(params);
      if (data.success && data.students) {
        setStudents(data.students);
        if (data.pagination) {
          setPagination({
            page: data.pagination.page,
            limit: data.pagination.limit,
            total: data.pagination.total,
            totalPages: data.pagination.totalPages
          });
        }
      } else {
        setStudents([]);
      }
    } catch (err) {
      showError(err.message || 'Failed to load students');
      setStudents([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBillingStudents(1);
  }, [filters.course, filters.session]);

  const handleSearch = () => {
    fetchBillingStudents(1);
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.totalPages) {
      fetchBillingStudents(newPage);
    }
  };

  const buildFourWeekRanges = (startDate, hardEndDate) => {
    const ranges = [];
    let cursor = new Date(startDate);
    const today = new Date();
    const providedEnd = hardEndDate ? new Date(hardEndDate) : null;
    const maxEnd = providedEnd && providedEnd < today ? providedEnd : today;
    while (cursor <= maxEnd) {
      const startDow = cursor.getDay();
      const firstWeekEndOffset = (5 - startDow + 7) % 7;
      const firstWeekEnd = addDays(cursor, firstWeekEndOffset);
      const firstMondayAfter = addDays(firstWeekEnd, 3);
      const fourthFriday = addDays(firstMondayAfter, 18);
      const naturalEnd = fourthFriday;
      const end = new Date(Math.min(naturalEnd.getTime(), maxEnd.getTime()));
      const urlStart = format(cursor, 'yyyy-MM-dd');
      const urlEnd = format(end, 'yyyy-MM-dd');
      ranges.push({ urlStart, urlEnd });
      cursor = addDays(end, 1);
    }
    return ranges;
  };

  const ensureTrainingStart = async (studentId) => {
    if (trainingPeriodByStudent[studentId] !== undefined) return trainingPeriodByStudent[studentId];
    try {
      const res = await fetch(`${API_BASE_URL}/attendance/sheet-data/${studentId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const json = await res.json();
      const start = json?.data?.voucher?.training_period_start_date;
      const end = json?.data?.voucher?.training_period_end_date;
      if (start) {
        const period = { start, end: end || null };
        setTrainingPeriodByStudent(prev => ({ ...prev, [studentId]: period }));
        return period;
      }
      setTrainingPeriodByStudent(prev => ({ ...prev, [studentId]: null }));
    } catch (_) {}
    return null;
  };

  const getAttendanceSheetUrl = (studentId, start, end) => {
    const sessionId = filters.session || (selectedSession && selectedSession.id);
    return `/admin/students/${studentId}/attendance-sheet?start=${start}&end=${end}${sessionId ? `&sessionId=${sessionId}` : ''}`;
  };

  const openAttendanceSheet = (studentId, start, end) => {
    window.open(getAttendanceSheetUrl(studentId, start, end), '_blank');
  };

  const openVoucher = (filePath) => {
    if (!filePath || !token) return;
    let cleanedPath = filePath;
    if (filePath.startsWith('admin/backend/uploads/')) {
      cleanedPath = filePath.replace('admin/backend/uploads/', '');
    } else if (filePath.includes('uploads/')) {
      cleanedPath = filePath.substring(filePath.indexOf('uploads/') + 'uploads/'.length);
    }
    if (cleanedPath.startsWith('admin/backend/')) cleanedPath = cleanedPath.replace('admin/backend/', '');
    const url = getSecureFileUrlWithToken(cleanedPath, token);
    window.open(url, '_blank');
  };

  const openInvoice = (filePath) => {
    if (!filePath || !token) return;
    let cleanedPath = filePath;
    if (filePath.startsWith('admin/backend/uploads/')) cleanedPath = filePath.replace('admin/backend/uploads/', '');
    else if (filePath.includes('uploads/')) cleanedPath = filePath.substring(filePath.indexOf('uploads/') + 'uploads/'.length);
    if (cleanedPath.startsWith('admin/backend/')) cleanedPath = cleanedPath.replace('admin/backend/', '');
    const url = getSecureFileUrlWithToken(cleanedPath, token);
    window.open(url, '_blank');
  };

  const openMarkBilledModal = (student) => {
    setModalStudent(student);
    setInvoiceFile(null);
    const d = new Date();
    setBillingPeriodMonth(String(d.getMonth() + 1));
    setBillingPeriodYear(String(d.getFullYear()));
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setModalStudent(null);
    setInvoiceFile(null);
  };

  const handleSubmitMarkBilled = async () => {
    if (!modalStudent || !invoiceFile) {
      showError('Please select a billing invoice file.');
      return;
    }
    setSubmitting(true);
    try {
      const formData = new FormData();
      formData.append('studentId', modalStudent.studentId);
      formData.append('billingInvoice', invoiceFile);
      if (billingPeriodMonth) formData.append('billingPeriodMonth', billingPeriodMonth);
      if (billingPeriodYear) formData.append('billingPeriodYear', billingPeriodYear);
      await markBilled(formData);
      showSuccess('Student marked as billed.');
      closeModal();
      fetchBillingStudents(pagination.page);
    } catch (err) {
      showError(err.message || 'Failed to add billing record');
    } finally {
      setSubmitting(false);
    }
  };

  const monthOptions = Array.from({ length: 12 }, (_, i) => ({ value: i + 1, label: new Date(2000, i, 1).toLocaleString('default', { month: 'long' }) }));
  const currentYear = new Date().getFullYear();
  const yearOptions = Array.from({ length: 6 }, (_, i) => currentYear - i);

  return (
    <Container>
      <Title>Billing</Title>
      <FilterContainer>
        <FilterRow>
          <FilterGroup>
            <Label>Course</Label>
            <Select value={filters.course} onChange={e => handleFilterChange('course', e.target.value)}>
              <option value="">--Select--</option>
              {masterCourses.map(c => (
                <option key={c.id} value={c.id}>{c.course_name} ({c.course_code})</option>
              ))}
            </Select>
          </FilterGroup>
          <FilterGroup>
            <Label>Session</Label>
            <Select
              value={filters.session}
              onChange={e => handleFilterChange('session', e.target.value)}
              disabled={!filters.course}
            >
              <option value="">{filters.course ? '--Select--' : 'Select a course first'}</option>
              {filteredSessions.map(s => (
                <option key={s.id} value={s.id}>{s.session_name || `Session ${s.id}`}</option>
              ))}
            </Select>
          </FilterGroup>
          <SearchButton onClick={handleSearch} disabled={loading}>
            {loading ? 'Searching...' : 'Search'}
          </SearchButton>
        </FilterRow>
        <SearchInputContainer>
          <SearchIcon><FaSearch /></SearchIcon>
          <SearchInput
            type="text"
            placeholder="Search by student name, email, or registration number..."
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleSearch()}
          />
          {searchTerm && (
            <ClearSearchIcon onClick={() => { setSearchTerm(''); fetchBillingStudents(1); }}>
              <FaTimes />
            </ClearSearchIcon>
          )}
        </SearchInputContainer>
      </FilterContainer>

      <ContentContainer>
        {loading ? (
          <LoadingSpinner>Loading...</LoadingSpinner>
        ) : students.length === 0 ? (
          <NoRecordsMessage>No students found. Adjust filters or search.</NoRecordsMessage>
        ) : (
          <>
            <TableWrapper>
            <Table>
              <thead>
                <tr>
                  <Th>ID</Th>
                  <Th>Student</Th>
                  <Th>Email</Th>
                  <Th>Enrollment date</Th>
                  <Th>Course</Th>
                  <Th>Attendance / Voucher</Th>
                  <Th>Billing</Th>
                </tr>
              </thead>
              <tbody>
                {students.map(student => {
                  const studentName = `${student.firstName || ''} ${student.lastName || ''}`.trim();
                  const isBilled = !!student.billingInvoicePath;
                  const enrollmentDate = student.enrollmentDate
                    ? format(new Date(student.enrollmentDate), 'yyyy-MM-dd')
                    : '—';
                  return (
                    <tr key={student.studentId}>
                      <Td>{student.studentId}</Td>
                      <Td>{studentName}</Td>
                      <Td>{student.email || '—'}</Td>
                      <Td>{enrollmentDate}</Td>
                      <TdCourse title={student.courseName || ''}>{student.courseName || '—'}</TdCourse>
                      <Td>
                        <AttendanceVoucherCell>
                          <AttendanceSheetCell
                            studentId={student.studentId}
                            trainingPeriodByStudent={trainingPeriodByStudent}
                            ensureTrainingStart={ensureTrainingStart}
                            buildFourWeekRanges={buildFourWeekRanges}
                            openAttendanceSheet={openAttendanceSheet}
                          />
                          {student.voucherFilePath ? (
                            <VoucherButton onClick={() => openVoucher(student.voucherFilePath)}>
                              <FaFileAlt /> View voucher
                            </VoucherButton>
                          ) : (
                            <span style={{ color: '#6c757d' }}>No voucher</span>
                          )}
                        </AttendanceVoucherCell>
                      </Td>
                      <Td>
                        <BillingCell>
                          <MarkBilledButton onClick={() => openMarkBilledModal(student)}>
                            <FaPlus /> Add billing record
                          </MarkBilledButton>
                          {isBilled && student.billingInvoicePath && (() => {
                            const monthNum = student.billingPeriodMonth || (student.billedAt ? new Date(student.billedAt).getMonth() + 1 : new Date().getMonth() + 1);
                            const year = student.billingPeriodYear || (student.billedAt ? new Date(student.billedAt).getFullYear() : new Date().getFullYear());
                            const monthName = new Date(2000, monthNum - 1, 1).toLocaleString('default', { month: 'long' });
                            return (
                              <InvoiceLink type="button" onClick={() => openInvoice(student.billingInvoicePath)}>
                                View invoice for {monthName} {year}
                              </InvoiceLink>
                            );
                          })()}
                        </BillingCell>
                      </Td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
            </TableWrapper>
            {pagination.totalPages > 1 && (
              <PaginationContainer>
                <PaginationInfo>
                  Showing {((pagination.page - 1) * pagination.limit) + 1} - {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total}
                </PaginationInfo>
                <PaginationButtons>
                  <PageButton onClick={() => handlePageChange(pagination.page - 1)} disabled={pagination.page === 1 || loading}>
                    Previous
                  </PageButton>
                  <PageButton onClick={() => handlePageChange(pagination.page + 1)} disabled={pagination.page >= pagination.totalPages || loading}>
                    Next
                  </PageButton>
                </PaginationButtons>
              </PaginationContainer>
            )}
          </>
        )}
      </ContentContainer>

      <Modal isOpen={modalOpen} onRequestClose={closeModal} style={ModalStyles} contentLabel="Add billing record">
        <ModalTitle>Add billing record {modalStudent ? `– ${modalStudent.firstName} ${modalStudent.lastName}` : ''}</ModalTitle>
        <FormGroup>
          <FormLabel>Billing invoice sheet (required)</FormLabel>
          <FileInput
            type="file"
            accept=".pdf,.jpg,.jpeg,.png,.gif,.webp"
            onChange={e => setInvoiceFile(e.target.files?.[0] || null)}
          />
        </FormGroup>
        <FormGroup>
          <FormLabel>Billing period (optional)</FormLabel>
          <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
            <Select
              value={billingPeriodMonth}
              onChange={e => setBillingPeriodMonth(e.target.value)}
              style={{ minWidth: '120px' }}
            >
              <option value="">Month</option>
              {monthOptions.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
            </Select>
            <Select
              value={billingPeriodYear}
              onChange={e => setBillingPeriodYear(e.target.value)}
              style={{ minWidth: '100px' }}
            >
              <option value="">Year</option>
              {yearOptions.map(y => <option key={y} value={y}>{y}</option>)}
            </Select>
          </div>
        </FormGroup>
        <ModalActions>
          <CancelButton onClick={closeModal}>Cancel</CancelButton>
          <SubmitButton onClick={handleSubmitMarkBilled} disabled={!invoiceFile || submitting}>
            {submitting ? 'Saving...' : 'Save'}
          </SubmitButton>
        </ModalActions>
      </Modal>
    </Container>
  );
};

function AttendanceSheetCell({ studentId, trainingPeriodByStudent, ensureTrainingStart, buildFourWeekRanges, openAttendanceSheet }) {
  const period = trainingPeriodByStudent[studentId];
  if (period === undefined) {
    ensureTrainingStart(studentId);
    return <span style={{ color: '#6c757d' }}>Loading...</span>;
  }
  if (!period || !period.start) {
    return <span style={{ color: '#6c757d' }}>No attendance period</span>;
  }
  const ranges = buildFourWeekRanges(new Date(period.start), period.end || null);
  if (ranges.length === 0) {
    return <span style={{ color: '#6c757d' }}>No sheets yet</span>;
  }
  const first = ranges[0];
  return (
    <AttendanceLinkButton onClick={() => openAttendanceSheet(studentId, first.urlStart, first.urlEnd)}>
      <FaCalendarAlt /> First Month Attendance Sheet
    </AttendanceLinkButton>
  );
}

export default Billing;
